import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Alert,
  Linking
} from 'react-native';
import { observer } from '@legendapp/state/react';
import { useRouter, useLocalSearchParams, Redirect } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SettingsSubscriptionLogger } from '../../../../utils/logger';
import theme from '../../../../config/theme';
import { profileScreenViewModel } from '../../../../state/computedStates';
import { isIOS, isWeb } from '../../../../utils/platform';
import { handleSuccess, cancelSubscription, getSubscription } from '../../../../services/subscriptionService';
import { globalState } from '../../../../state/globalState';
import { useWebAlert } from '../../../../components/WebAlert';
import WebLoadingOverlay from '../../../../components/WebLoadingOverlay';
import { APP_CONSTANTS } from '../../../../config/constants';
import {
  getSettingsContentPaddingBottom,
  getSettingsHeaderPaddingTop,
} from '@/utils/safeArea';
import { canAccessFinancialSettings } from '@/utils/userRoles';

// Logger para a página de configurações de assinatura
const logger = SettingsSubscriptionLogger.Main;

// Interface para os detalhes de assinatura retornados pela API do Stripe
interface SubscriptionDetails {
  id: string;                       // ID local (UUID Supabase)
  externalId?: string;              // ID externo (Stripe)
  status: string;                   // Status da assinatura (normalizado para o app)
  currentPeriodEnd: number | null;  // Timestamp do final do período atual
  startDate?: string | null;        // Data de início
  nextBillingDate?: string | null;  // Data da próxima cobrança
  end_date?: string | null;         // Data de término (final do período se cancelado)
  error?: string;                   // Erro, se houver
  billingProvider?: string;
  isManual?: boolean;
  paymentMethod?: string;
}

// Função utilitária para mapear assinatura para detalhes
function mapSubscriptionToDetails(sub: any): SubscriptionDetails & { externalId?: string } {
  return {
    id: sub.id, // UUID local
    externalId: sub.externalId || '', // ID externo (Stripe)
    status: sub.status,
    currentPeriodEnd: sub.nextBillingDate ? new Date(sub.nextBillingDate).getTime() : null,
    startDate: sub.startDate || null,
    nextBillingDate: sub.nextBillingDate || null,
    end_date: sub.expiration || null,
    billingProvider: sub.billingProvider,
    isManual: sub.isManual,
    paymentMethod: sub.paymentMethod,
  };
}

// Renderiza o status da assinatura com cor apropriada
const SubscriptionStatus = ({ status }: { status: string }) => {
  let color = theme.colors.util.warning;
  let text = 'Pendente';
  
  switch (status.toLowerCase()) {
    case 'ativa':
    case 'active':
      color = theme.colors.util.success;
      text = 'Ativa';
      break;
    case 'inativa':
    case 'inactive':
      color = theme.colors.util.error;
      text = 'Inativa';
      break;
    case 'atrasado':
    case 'past_due':
      color = theme.colors.util.error;
      text = 'Pagamento atrasado';
      break;
    case 'cancelada':
    case 'canceled':
      color = theme.colors.text.tertiary;
      text = 'Cancelada';
      break;
    case 'cancelamento_agendado':
      color = theme.colors.util.warning;
      text = 'Cancelamento agendado';
      break;
    default:
      break;
  }
  
  return (
    <View style={[styles.statusBadge, { backgroundColor: color }]}>
      <Text style={styles.statusText}>{text}</Text>
    </View>
  );
};

// Página principal de configurações de assinatura
const SubscriptionScreen = observer(() => {
  const router = useRouter();
  const safeAreaInsets = useSafeAreaInsets();
  const headerPaddingTop = getSettingsHeaderPaddingTop(safeAreaInsets.top);
  const scrollViewBottomPadding = getSettingsContentPaddingBottom(safeAreaInsets.bottom);
  const params = useLocalSearchParams();
  const userType = globalState.user.profile.get()?.user_type;
  const hasScreenAccess = canAccessFinancialSettings(userType);

  const profileView = profileScreenViewModel.get();
  const isAuthenticated = globalState.user.isAuthenticated.get();
  const isLoading = globalState.user.isLoading.get();
  const subscriptionState = globalState.subscription.get();
  const billingProvider = subscriptionState.billingProvider;
  const isAppleSubscription =
    billingProvider === 'apple' ||
    subscriptionState.paymentMethod === 'apple_iap' ||
    subscriptionState.externalId?.startsWith('manual:apple:');
  const isManualSubscription = subscriptionState.isManual && !isAppleSubscription;
  const isTrialSubscription = subscriptionState.externalId?.startsWith(
    APP_CONSTANTS.SUBSCRIPTION_TRIAL.EXTERNAL_ID_PREFIX,
  ) === true;
  const hasActivePaidSubscription =
    (subscriptionState.status === 'ativa' || subscriptionState.status === 'cancelamento_agendado') &&
    Boolean(subscriptionState.externalId) &&
    !isTrialSubscription;
  const paymentMethodLabel = (() => {
    if (isAppleSubscription) return 'App Store';
    if (isManualSubscription) return 'PIX (manual)';
    if (subscriptionState.paymentMethod === 'stripe') return 'Cartão de crédito';
    if (subscriptionState.paymentMethod === 'pix') return 'PIX';
    return 'Método não informado';
  })();
  const [isProcessingSession, setIsProcessingSession] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [processingResult, setProcessingResult] = useState<{
    status: 'success' | 'error';
    message: string;
  } | null>(null);
  const { showAlert } = useWebAlert();
  const isIOSCompanionMode = isIOS;
  const screenTitle = isIOSCompanionMode ? 'Acesso da conta' : 'Assinatura';

  // Função para formatar datas com segurança
  const formatarData = (dataString?: string | null) => {
    if (!dataString) return 'Não definida';
    
    try {
      const data = new Date(dataString);
      return !isNaN(data.getTime()) ? 
        data.toLocaleDateString('pt-BR') : 
        'Data não disponível';
    } catch (e) {
      return 'Data não disponível';
    }
  };

  // Buscar detalhes da assinatura atual
  useEffect(() => {
    if (!hasScreenAccess || isIOSCompanionMode) {
      return;
    }

    const loadSubscriptionDetails = async () => {
      if (profileView?.subscription?.isActive) {
        try {
          setIsLoadingSubscription(true);
          // getSubscription tentará obter o ID da assinatura do estado global
          const details = await getSubscription();
          setSubscriptionDetails(mapSubscriptionToDetails(details));
          logger.info('Detalhes da assinatura carregados:', mapSubscriptionToDetails(details));
        } catch (error) {
          logger.error('Erro ao carregar detalhes da assinatura:', error);
          setProcessingResult({
            status: 'error',
            message: 'Não foi possível carregar os detalhes da assinatura. Tente novamente mais tarde.'
          });
        } finally {
          setIsLoadingSubscription(false);
        }
      }
    };
    
    loadSubscriptionDetails();
  }, [hasScreenAccess, isIOSCompanionMode, profileView?.subscription?.isActive]);

  // Processar session_id se disponível na URL (retorno do Stripe Checkout)
  useEffect(() => {
    if (!hasScreenAccess) {
      return;
    }

    const sessionId = params.session_id as string;
    
    if (isWeb && sessionId) {
      logger.info(`Processando retorno do Stripe com session_id: ${sessionId}`);
      
      const processCheckoutSession = async () => {
        try {
          setIsProcessingSession(true);
          
          // Chamar a função de serviço para confirmar o pagamento
          const result = await handleSuccess(sessionId);
          
          logger.info(`Pagamento confirmado com sucesso! Status: ${result.status}`, result);
          
          setProcessingResult({
            status: 'success',
            message: 'Assinatura confirmada com sucesso! Seu plano já está ativo.'
          });
          
          // Atualizar detalhes da assinatura
          try {
            const details = await getSubscription();
            setSubscriptionDetails(mapSubscriptionToDetails(details));
          } catch (err) {
            logger.warn('Erro ao atualizar detalhes da assinatura após confirmação:', err);
          }
          
          // Limpar o parâmetro session_id da URL 
          // (para evitar que o usuário atualize a página e processe novamente)
          router.setParams({});
        } catch (error) {
          logger.error('Erro ao processar pagamento:', error);
          setProcessingResult({
            status: 'error',
            message: 'Ocorreu um erro ao confirmar seu pagamento. Entre em contato com o suporte.'
          });
        } finally {
          setIsProcessingSession(false);
        }
      };
      
      processCheckoutSession();
    }
  }, [params.session_id]);
  
  useEffect(() => {
    if (!hasScreenAccess) {
      return;
    }

    logger.info('Página de configurações de assinatura inicializada');
    return () => {
      logger.info('Página de configurações de assinatura desmontada');
    };
  }, []);
  
  // Funções para ações
  const handleChangePlan = async () => {
    try {
      if (isIOSCompanionMode) {
        if (isAppleSubscription) {
          await Linking.openURL('https://apps.apple.com/account/subscriptions');
        } else if (hasActivePaidSubscription) {
          await showAlert(
            'Plano ja ativo',
            'Sua empresa ja possui acesso Premium. Para evitar cobranca duplicada, uma nova assinatura Apple nao sera iniciada.',
            [{ text: 'OK' }],
          );
        } else {
          router.push('/settings/subscription/plans');
        }
        return;
      }

      if (isManualSubscription) {
        const manualMessage = 'Esta assinatura é gerenciada manualmente via PIX. Use o painel do desenvolvedor para alterar o plano ou entre em contato com o responsável pela conta.';
        if (Platform.OS !== 'android') {
          await showAlert('Gestão manual', manualMessage, [{ text: 'OK' }]);
        } else {
          Alert.alert('Gestão manual', manualMessage);
        }
        return;
      }

      setIsChangingPlan(true);
      // Navegar para plans page
      router.push('/settings/subscription/plans');
    } catch (error) {
      logger.error('Erro ao navegar para página de planos:', error);
    } finally {
      setIsChangingPlan(false);
    }
  };
  
  const handleViewPaymentHistory = () => {
    router.push('/settings/payments');
  };
  
  const handleCancelSubscription = async () => {
    if (isAppleSubscription || subscriptionDetails?.billingProvider === 'apple' || subscriptionDetails?.externalId?.startsWith('manual:apple:')) {
      const appleCancelMessage = 'Esta assinatura é gerenciada pela App Store. Use a tela de assinaturas da Apple para cancelar ou alterar a cobrança.';
      if (Platform.OS !== 'android') {
        await showAlert('Gerenciar na Apple', appleCancelMessage, [
          { text: 'Fechar', style: 'cancel' },
          {
            text: 'Abrir App Store',
            onPress: () => Linking.openURL('https://apps.apple.com/account/subscriptions'),
          },
        ]);
      } else {
        Alert.alert('Gerenciar na Apple', appleCancelMessage);
      }
      return;
    }

    if (isManualSubscription || subscriptionDetails?.externalId?.startsWith('manual:')) {
      const manualCancelMessage = 'Este plano é administrado manualmente via PIX. Utilize o painel do desenvolvedor para encerrar ou ajustar a assinatura.';
      if (Platform.OS !== 'android') {
        await showAlert('Gestão manual', manualCancelMessage, [{ text: 'OK' }]);
      } else {
        Alert.alert('Gestão manual', manualCancelMessage);
      }
      return;
    }

    // Implementar lógica de cancelamento
    logger.info('Cancelamento de assinatura solicitado');
    
    const confirmCancel = async () => {
      if (Platform.OS !== 'android') {
        return new Promise((resolve) => {
          showAlert(
            "Confirmar cancelamento",
            "Tem certeza que deseja cancelar sua assinatura? Você manterá o acesso até o final do período já pago.",
            [
              {
                text: "Não",
                style: "cancel",
                onPress: () => resolve(false)
              },
              { 
                text: "Sim, cancelar", 
                style: "destructive",
                onPress: () => resolve(true)
              }
            ]
          );
        });
      } else {
        return new Promise((resolve) => {
          Alert.alert(
            "Confirmar cancelamento",
            "Tem certeza que deseja cancelar sua assinatura? Você manterá o acesso até o final do período já pago.",
            [
              {
                text: "Não",
                style: "cancel",
                onPress: () => resolve(false)
              },
              { 
                text: "Sim, cancelar", 
                style: "destructive",
                onPress: () => resolve(true)
              }
            ]
          );
        });
      }
    };
    
    const shouldCancel = await confirmCancel();
    
    if (shouldCancel) {
      try {
        // Obter o ID de assinatura externa
        const companyData = globalState.company.data.get();
        
        // Priorizar o ID de dentro dos detalhes da assinatura, que é o ID externo do Stripe
        const stripeSubscriptionId = (
          subscriptionDetails?.externalId || // Agora usa explicitamente o externalId
          companyData?.stripe_customer_id || 
          ''
        );
        
        if (stripeSubscriptionId) {
          setIsProcessingSession(true);
          
          // Cancelar usando o ID externo do Stripe
          logger.info(`Cancelando assinatura no Stripe usando external_id: ${stripeSubscriptionId}`);
          await cancelSubscription(stripeSubscriptionId);
          
          // Mostrar feedback visual para o usuário
          if (Platform.OS !== 'android') {
            await showAlert(
              "Assinatura cancelada",
              "Sua assinatura foi cancelada com sucesso. Você continuará com acesso Premium até o final do período já pago.",
              [{ text: "OK" }]
            );
          } else {
            Alert.alert(
              "Assinatura cancelada",
              "Sua assinatura foi cancelada com sucesso. Você continuará com acesso Premium até o final do período já pago."
            );
          }
          
          // Recarregar os detalhes atualizados da assinatura
          try {
            const details = await getSubscription(undefined, true); // force refresh
            setSubscriptionDetails(mapSubscriptionToDetails(details));
          } catch (err) {
            logger.warn('Erro ao recarregar detalhes da assinatura após cancelamento:', err);
          }
          
          setProcessingResult({
            status: 'success',
            message: 'Assinatura cancelada com sucesso. Você manterá o acesso Premium até o final do período já pago.'
          });
        } else {
          throw new Error('ID de assinatura não encontrado');
        }
      } catch (error) {
        logger.error('Erro ao cancelar assinatura:', error);
        
        // Mostrar feedback visual para o usuário
        if (Platform.OS !== 'android') {
          await showAlert(
            "Erro ao cancelar",
            "Não foi possível cancelar sua assinatura. Entre em contato com o suporte.",
            [{ text: "OK" }]
          );
        } else {
          Alert.alert(
            "Erro ao cancelar",
            "Não foi possível cancelar sua assinatura. Entre em contato com o suporte."
          );
        }
        
        setProcessingResult({
          status: 'error',
          message: 'Não foi possível cancelar sua assinatura. Entre em contato com o suporte.'
        });
      } finally {
        setIsProcessingSession(false);
      }
    }
  };
  
  // Navegação para voltar
  const handleGoBack = () => {
    router.back();
  };
  
  // Verificar autenticação - redirecionar para login se não estiver autenticado
  if (!hasScreenAccess) {
    return <Redirect href="/(app)/settings" />;
  }

  if (!isAuthenticated && !isLoading) {
    logger.info('Usuário não autenticado, redirecionando para login');
    return <Redirect href="/(auth)/login" />;
  }
  
  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary.default} />
        <Text style={styles.loadingText}>Inicializando...</Text>
      </View>
    );
  }
  
  if (isProcessingSession) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary.default} />
        <Text style={styles.loadingText}>Processando solicitação...</Text>
      </View>
    );
  }
  
  if (!profileView) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary.default} />
        <Text style={styles.loadingText}>Carregando informações...</Text>
      </View>
    );
  }
  
  const { subscription } = profileView;
  
  // Mostrar loader durante a busca dos detalhes da assinatura
  const showLoader = !isIOSCompanionMode && isLoadingSubscription && subscription.isActive && subscription.isPremium;
  const isChangePlanDisabled =
    isChangingPlan ||
    (isManualSubscription && !isIOSCompanionMode) ||
    (isIOSCompanionMode && hasActivePaidSubscription && !isAppleSubscription);
  
  return (
    <View style={styles.container}>
      <WebLoadingOverlay
        visible={isChangingPlan && Platform.OS === 'web'}
        message="Carregando planos. Por favor, aguarde enquanto preparamos os planos disponíveis..."
      />
      <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={handleGoBack}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{screenTitle}</Text>
        <View style={styles.headerRight} />
      </View>
      
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollViewContent, { paddingBottom: scrollViewBottomPadding }]}
        showsVerticalScrollIndicator={false}
      >
        {processingResult && (
          <View style={[
            styles.alertContainer,
            { backgroundColor: processingResult.status === 'success' 
              ? 'rgba(25, 190, 129, 0.15)'
              : 'rgba(237, 69, 93, 0.15)'
            }
          ]}>
            <MaterialCommunityIcons 
              name={processingResult.status === 'success' ? 'check-circle' : 'alert-circle-outline'} 
              size={24} 
              color={processingResult.status === 'success' 
                ? theme.colors.util.success 
                : theme.colors.util.error
              } 
            />
            <Text style={styles.alertText}>{processingResult.message}</Text>
          </View>
        )}
        
        {showLoader ? (
          <View style={styles.loadingSection}>
            <ActivityIndicator size="large" color={theme.colors.primary.default} />
            <Text style={styles.loadingText}>Carregando detalhes da assinatura...</Text>
          </View>
        ) : (
          <>
            {/* Seção de plano atual */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{isIOSCompanionMode ? 'Status do acesso' : 'Plano atual'}</Text>
              
              <View style={styles.planHeader}>
                <View style={[
                  styles.planBadge, 
                  { backgroundColor: subscription.isPremium ? theme.colors.primary.default : theme.colors.secondary.default }
                ]}>
                  <Text style={styles.planBadgeText}>
                    {subscription.isPremium ? 'Premium' : 'Free'}
                  </Text>
                </View>
                
                <SubscriptionStatus status={
                  subscriptionDetails?.status || 
                  (subscription.isActive ? 'ativa' : 'inativa')
                } />
              </View>
              
              {isIOSCompanionMode ? (
                <View style={styles.freePlanMessage}>
                  <MaterialCommunityIcons name="information-outline" size={24} color={theme.colors.text.secondary} />
                  <Text style={styles.freePlanText}>
                    {subscription.isPremium
                      ? 'Sua conta está habilitada para usar o App Guincho no iPhone. Se notar qualquer divergência de acesso, fale com o suporte.'
                      : 'No iPhone, o App Guincho funciona como aplicativo complementar para contas empresariais já habilitadas. Se você acredita que seu acesso deveria estar ativo, fale com o suporte.'}
                  </Text>
                </View>
              ) : subscription.isPremium ? (
                // Mostrar detalhes da assinatura premium
                <View style={styles.planInfo}>
                  <View style={styles.planInfoRow}>
                    <Text style={styles.planInfoLabel}>Status:</Text>
                    <Text style={styles.planInfoValue}>
                      {subscriptionDetails?.status === 'cancelamento_agendado' 
                        ? 'Cancelamento agendado' 
                        : subscriptionDetails?.status || (subscription.isActive ? 'Ativa' : 'Inativa')}
                    </Text>
                  </View>
                  
                  <View style={styles.planInfoRow}>
                    <Text style={styles.planInfoLabel}>Início:</Text>
                    <Text style={styles.planInfoValue}>
                      {subscriptionDetails?.startDate ? 
                        new Date(subscriptionDetails.startDate).toLocaleDateString('pt-BR') : 
                        'Não disponível'
                      }
                    </Text>
                  </View>
                  
                  <View style={styles.planInfoRow}>
                    <Text style={styles.planInfoLabel}>
                      {subscriptionDetails?.status === 'cancelamento_agendado' ? 'Acesso até:' : 'Renovação:'}
                    </Text>
                    <Text style={styles.planInfoValue}>
                      {subscriptionDetails?.status === 'cancelamento_agendado' && subscriptionDetails?.end_date ? 
                        formatarData(subscriptionDetails.end_date) :
                        formatarData(subscriptionDetails?.nextBillingDate) || 
                        (subscription.expirationFormatted || 'Não definida')
                      }
                    </Text>
                  </View>
                  {/* Nova linha: sempre mostra a data de renovação (nextBillingDate) */}
                  <View style={styles.planInfoRow}>
                    <Text style={styles.planInfoLabel}>NEXT BILLING DATE:</Text>
                    <Text style={styles.planInfoValue}>
                      {formatarData(subscriptionDetails?.nextBillingDate)}
                    </Text>
                  </View>
                  
                  <View style={styles.planInfoRow}>
                    <Text style={styles.planInfoLabel}>Método:</Text>
                    <Text style={styles.planInfoValue}>{paymentMethodLabel}</Text>
                  </View>

                  {isManualSubscription && !isIOSCompanionMode && (
                    <View style={styles.manualInfoBox}>
                      <MaterialCommunityIcons name="whatsapp" size={22} color="#25D366" />
                      <View style={styles.manualInfoTextWrapper}>
                        <Text style={styles.manualInfoTitle}>Pagamento via PIX</Text>
                        <Text style={styles.manualInfoText}>
                          Entre em contato conosco pelo WhatsApp para atualizar o pagamento ou solicitar cancelamento.
                        </Text>
                        <TouchableOpacity
                          style={styles.manualInfoAction}
                          onPress={() => Linking.openURL(APP_CONSTANTS.CONTACTS.WHATSAPP_URL)}
                        >
                          <Text style={styles.manualInfoActionText}>
                            Falar com suporte
                          </Text>
                          <MaterialCommunityIcons name="open-in-new" size={16} color={theme.colors.primary.default} />
                        </TouchableOpacity>
                      </View>
                    </View>
                  )}
                  
                  {subscriptionDetails?.status === 'cancelamento_agendado' && (
                    <View style={styles.cancelMessage}>
                      <MaterialCommunityIcons name="information-outline" size={24} color={theme.colors.util.warning} />
                      <Text style={styles.cancelMessageText}>
                        Sua assinatura foi cancelada, mas você continuará com acesso Premium até o final do período já pago.
                      </Text>
                    </View>
                  )}
                </View>
              ) : (
                // Mostrar mensagem para plano free
                <View style={styles.freePlanMessage}>
                  <MaterialCommunityIcons name="information-outline" size={24} color={theme.colors.text.secondary} />
                  <Text style={styles.freePlanText}>
                    Você está utilizando a versão gratuita do aplicativo. Faça upgrade para o plano Premium e aproveite todos os recursos disponíveis.
                  </Text>
                </View>
              )}
              
              <TouchableOpacity 
                style={[styles.actionButton, isChangePlanDisabled && styles.disabledButton]}
                onPress={handleChangePlan}
                disabled={isChangePlanDisabled}
              >
                {isChangingPlan ? (
                  <View style={styles.buttonLoadingContainer}>
                    <ActivityIndicator size="small" color={theme.colors.text.inverse} />
                    <Text style={styles.actionButtonText}>
                      {subscription.isPremium ? 'Carregando...' : 'Carregando...'}
                    </Text>
                  </View>
                ) : (
                  <Text style={styles.actionButtonText}>
                    {isManualSubscription
                      ? isIOSCompanionMode
                        ? 'Assinar pela App Store'
                        : 'Gerenciado manualmente'
                      : isIOSCompanionMode
                        ? isAppleSubscription
                          ? 'Gerenciar na App Store'
                          : hasActivePaidSubscription
                          ? 'Plano ja ativo'
                          : 'Assinar pela App Store'
                        : subscription.isPremium
                          ? 'Alterar plano'
                          : 'Fazer upgrade'}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
            
            {/* Seção de histórico de pagamentos - apenas para planos premium */}
            {!isIOSCompanionMode && subscription.isPremium && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Pagamentos</Text>
                
                <TouchableOpacity 
                  style={styles.actionButton}
                  onPress={handleViewPaymentHistory}
                >
                  <Text style={styles.actionButtonText}>Ver histórico de pagamentos</Text>
                </TouchableOpacity>
              </View>
            )}
            
            {/* Seção de cancelamento - apenas para planos premium ativos e não cancelados */}
            {!isIOSCompanionMode && subscription.isPremium && subscription.isActive && !isManualSubscription &&
             subscriptionDetails?.status !== 'cancelamento_agendado' && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Gerenciar assinatura</Text>
                
                <TouchableOpacity 
                  style={styles.cancelButton}
                  onPress={handleCancelSubscription}
                >
                  <Text style={styles.cancelButtonText}>Cancelar assinatura</Text>
                </TouchableOpacity>
                
                <Text style={styles.cancelNote}>
                  O cancelamento será efetivado ao final do período já pago.
                </Text>
              </View>
            )}
          </>
        )}
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
  },
  loadingText: {
    marginTop: theme.spacing.md,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
  },
  header: {
    backgroundColor: theme.colors.primary.default,
    padding: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? theme.spacing.xl + theme.spacing.sm : theme.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: theme.fontSizes.xl,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.inverse,
  },
  headerRight: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    paddingBottom: theme.spacing.xl,
  },
  alertContainer: {
    margin: theme.spacing.md,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    flexDirection: 'row',
    alignItems: 'center',
  },
  alertText: {
    marginLeft: theme.spacing.sm,
    fontSize: theme.fontSizes.md,
    flex: 1,
    color: theme.colors.text.primary,
  },
  section: {
    backgroundColor: theme.colors.background.primary,
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.lg,
    ...theme.shadows.md,
  },
  sectionTitle: {
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.md,
  },
  planHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  planBadge: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.circular,
  },
  planBadgeText: {
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.inverse,
  },
  statusBadge: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.circular,
  },
  statusText: {
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.inverse,
  },
  planInfo: {
    marginBottom: theme.spacing.md,
  },
  planInfoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.sm,
  },
  planInfoLabel: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
  },
  planInfoValue: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.primary,
  },
  manualInfoBox: {
    marginTop: theme.spacing.md,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    backgroundColor: 'rgba(250, 250, 250, 0.6)',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  manualInfoTextWrapper: {
    flex: 1,
    marginLeft: theme.spacing.sm,
  },
  manualInfoTitle: {
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.xs,
  },
  manualInfoText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    lineHeight: 18,
  },
  manualInfoAction: {
    marginTop: theme.spacing.xs,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: theme.spacing.xs,
  },
  manualInfoActionText: {
    marginRight: theme.spacing.xs,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.primary.default,
    fontWeight: theme.fontWeights.semibold as any,
  },
  actionButton: {
    backgroundColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    alignItems: 'center',
    marginTop: theme.spacing.sm,
  },
  disabledButton: {
    backgroundColor: theme.colors.primary.light,
    opacity: 0.8,
  },
  buttonLoadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
  },
  cancelButton: {
    backgroundColor: theme.colors.util.error,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    alignItems: 'center',
  },
  cancelButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
  },
  cancelNote: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.tertiary,
    marginTop: theme.spacing.sm,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  loadingSection: {
    backgroundColor: theme.colors.background.primary,
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
    ...theme.shadows.md,
  },
  freePlanMessage: {
    marginVertical: theme.spacing.md,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    backgroundColor: 'rgba(250, 250, 250, 0.5)',
    flexDirection: 'row',
    alignItems: 'center',
  },
  freePlanText: {
    marginLeft: theme.spacing.sm,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    flex: 1,
    lineHeight: 20,
  },
  cancelMessage: {
    marginTop: theme.spacing.md,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    backgroundColor: 'rgba(250, 250, 250, 0.5)',
    flexDirection: 'row',
    alignItems: 'center',
  },
  cancelMessageText: {
    marginLeft: theme.spacing.sm,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    flex: 1,
  },
});

export default SubscriptionScreen; 
